import { useCallback, useEffect, useState } from 'react'
import { ErroreApi } from '../../servizi/api.ts'
import { Attesa, Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Campo, Etichetta, Modale, Spunta, Tabella } from '../../componenti/base.tsx'
import * as posta from '../../servizi/posta.ts'

/**
 * Quarantena degli allegati infetti (546, decisione di Davide del 3/11/2026): la linguetta per la Direzione e
 * la Referente privacy (`posta.quarantena_gestisci`).
 *
 * Gli allegati che l'antivirus ha dichiarato infetti stanno in un bucket solo per loro, in Glacier; nella
 * posta chi apre il messaggio vede il nome e «Infetto, in quarantena: non si scarica». Qui:
 *  - l'elenco con casella, data e mittente del messaggio, nome, tipo, peso e data della quarantena;
 *  - «Scarica in formato sicuro»: con gli avvisi e il motivo si chiede il recupero; il lavoro pianificato
 *    riporta il file da Glacier (di solito entro 12 ore) e ne fa uno zip cifrato con password, che si
 *    scarica per qualche ora; la password si mostra una volta, al primo scaricamento;
 *  - «Elimina definitivamente», con il nome del file scritto per conferma.
 * Ogni richiesta, scaricamento ed eliminazione resta nel registro; delle richieste e degli scaricamenti la
 * Referente privacy riceve un avviso.
 * La pagina si aggiorna da sola ogni minuto finche' c'e' un recupero in corso.
 */

const testoErrore = (e: unknown) =>
  (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

const giorno = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
const giornoOra = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
const data = (iso: string) => giorno.format(new Date(iso))
const quando = (iso: string) => giornoOra.format(new Date(iso))
const soloGiorno = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', timeZone: 'Europe/Rome' })
const soloOra = new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' })
/**
 * Giorno e ora dentro una frase, con l'articolo giusto: «entro il 3 nov alle 23:26», «entro l’11 nov…», «fino al
 * 1° nov…», «fino all’8 nov…».
 */
function giornoAlle(iso: string, preposizione: 'entro' | 'fino'): string {
  const d = new Date(iso)
  const n = Number(new Intl.DateTimeFormat('it-IT', { day: 'numeric', timeZone: 'Europe/Rome' }).format(d))
  const resto = soloGiorno.format(d).replace(/^\d+/, n === 1 ? '1°' : String(n))
  const articolo = n === 8 || n === 11 ? (preposizione === 'entro' ? 'l’' : 'all’') : preposizione === 'entro' ? 'il ' : 'al '
  return `${preposizione} ${articolo}${resto} alle ${soloOra.format(d)}`
}

const PERICOLO = 'Contiene un programma dannoso. Non aprirlo sul computer di lavoro; serve solo a chi lo analizza o a un’autorità.'

const inCorso = (r: posta.RecuperoSicuro) => r.stato === 'in_coda' || r.stato === 'in_recupero'
const valido = (r: posta.RecuperoSicuro) => r.stato === 'pronto' && !!r.zip_scade_il && new Date(r.zip_scade_il) > new Date()

function StatoRecupero({ r }: { r: posta.RecuperoSicuro }) {
  const chi = r.mio ? '' : ` · chiesto da ${r.chiesto_da}`
  if (r.stato === 'in_coda') {
    return <span className="piccolo"><Etichetta tono="neutro">in coda</Etichetta> il ritorno da Glacier parte entro un quarto d’ora{chi}</span>
  }
  if (r.stato === 'in_recupero') {
    return (
      <span className="piccolo">
        <Etichetta tono="accento">da Glacier</Etichetta>{' '}
        {`pronto di solito ${giornoAlle(r.stimato_il, 'entro')}, al più tardi ${giornoAlle(r.massimo_il, 'entro')}`}{chi}
      </span>
    )
  }
  if (r.stato === 'pronto' && r.zip_scade_il) {
    return (
      <span className="piccolo">
        <Etichetta tono={valido(r) ? 'positivo' : 'neutro'}>zip pronto</Etichetta> si scarica {giornoAlle(r.zip_scade_il, 'fino')}
        {r.scaricamenti > 0 && ` · ${r.scaricamenti === 1 ? '1 scaricamento' : `${r.scaricamenti} scaricamenti`}`}{chi}
      </span>
    )
  }
  if (r.stato === 'scaduto') return <span className="piccolo tenue">zip scaduto e cancellato{chi}</span>
  if (r.stato === 'annullato') return <span className="piccolo tenue">annullato: file eliminato dalla quarantena{chi}</span>
  return <span className="piccolo testo-critico">recupero non riuscito: si può chiedere di nuovo{chi}</span>
}

export function Quarantena() {
  const [q, setQ] = useState<posta.Quarantena | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const [eliminati, setEliminati] = useState(false)
  const [daRecuperare, setDaRecuperare] = useState<posta.VoceQuarantena | null>(null)
  const [daEliminare, setDaEliminare] = useState<posta.VoceQuarantena | null>(null)
  const [daScaricare, setDaScaricare] = useState<{ voce: posta.VoceQuarantena; recupero: posta.RecuperoSicuro } | null>(null)

  const carica = useCallback(() => {
    posta.leggiQuarantena().then((r) => { setQ(r); setErrore(null) }).catch((e) => setErrore(testoErrore(e)))
  }, [])
  useEffect(() => { carica() }, [carica])
  // Con un recupero in corso la pagina si rilegge ogni minuto: il lavoro aggiorna lo stato ogni quarto d'ora.
  const seguire = !!q?.voci.some((v) => v.recuperi.some(inCorso))
  useEffect(() => {
    if (!seguire) return
    const t = window.setInterval(carica, 60_000)
    return () => window.clearInterval(t)
  }, [seguire, carica])

  const voci = (q?.voci ?? []).filter((v) => eliminati || !v.eliminata)
  const quantiEliminati = (q?.voci ?? []).filter((v) => v.eliminata).length

  return (
    <div>
      <div className="posta-titoletto">Quarantena degli allegati infetti</div>
      <p className="piccolo tenue">
        Gli allegati che l’antivirus ha dichiarato infetti, tolti dalla posta e conservati a parte, in Glacier. Nei
        messaggi resta il nome, con la scritta «{posta.SCRITTA_QUARANTENA}». Restano qui finché qualcuno non li elimina:
        non c’è cancellazione automatica. Ogni recupero, scaricamento ed eliminazione resta nel registro eventi; dei
        recuperi e degli scaricamenti la Referente privacy riceve un avviso.
      </p>
      {q && !q.attiva && (
        <Avviso tipo="attenzione">
          La quarantena automatica non è ancora attiva: la accende chi gestisce il sistema, quando sposta gli
          allegati già infetti. Fino ad allora gli allegati infetti restano bloccati nella posta, ma non spostati.
        </Avviso>
      )}
      {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!q && !errore && <Attesa testo="Caricamento…" />}
      {q && quantiEliminati > 0 && (
        <Spunta etichetta={`Mostra anche gli eliminati (${quantiEliminati})`} valore={eliminati} cambia={setEliminati} />
      )}
      {q && (
        <Tabella intestazioni={['Allegato', 'Messaggio', 'In quarantena', 'Recupero in formato sicuro',
                                <span className="solo-lettore" key="azioni">Azioni</span>]}
                 vuoto={<Vuoto testo="Nessun allegato in quarantena." />}>
          {voci.map((v) => {
            const mio = v.recuperi.find((r) => r.mio && (inCorso(r) || valido(r)))
            return (
              <tr key={v.id}>
                <td data-etichetta="Allegato">
                  <div className="voce-principale">{v.nome}</div>
                  <div className="piccolo tenue">{v.mime} · {posta.peso(v.dimensione)}</div>
                  {v.eliminata
                    ? <div className="piccolo testo-critico">eliminato dalla quarantena il {data(v.eliminata.il)} da {v.eliminata.da}</div>
                    : <div className="piccolo testo-critico">{posta.SCRITTA_QUARANTENA}</div>}
                </td>
                <td data-etichetta="Messaggio" className="piccolo">
                  <div>{v.casella ?? 'casella non più presente'}</div>
                  <div className="tenue">da {v.mittente ?? 'mittente non leggibile'}</div>
                  {v.data_messaggio && <div className="tenue">{quando(v.data_messaggio)}</div>}
                </td>
                <td data-etichetta="In quarantena" className="piccolo">
                  {data(v.messa_il)}
                  <div className="tenue">{v.messa_da ? `da ${v.messa_da}` : 'dall’antivirus'}</div>
                  {v.origine_da_pulire && !v.eliminata && (
                    <div className="tenue">copie nel bucket dei documenti in cancellazione</div>
                  )}
                </td>
                <td data-etichetta="Recupero in formato sicuro">
                  {v.recuperi.length === 0 ? <span className="piccolo tenue">nessuno</span> : (
                    <ul className="quarantena-recuperi">
                      {v.recuperi.map((r) => (
                        <li key={r.id}>
                          <StatoRecupero r={r} />
                          <div className="piccolo tenue">{quando(r.chiesto_il)} · «{r.motivo}»</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                <td data-etichetta="Azioni">
                  {!v.eliminata && (
                    <div className="quarantena-comandi">
                      {mio && valido(mio) ? (
                        <Bottone dimensione="piccolo" variante="primario"
                                 onClick={() => { setErrore(null); setMessaggio(null); setDaScaricare({ voce: v, recupero: mio }) }}>
                          Scarica lo zip
                        </Bottone>
                      ) : !mio && (
                        <Bottone dimensione="piccolo" variante="secondario"
                                 onClick={() => { setErrore(null); setMessaggio(null); setDaRecuperare(v) }}>
                          Scarica in formato sicuro
                        </Bottone>
                      )}
                      <Bottone dimensione="piccolo" variante="discreto" aria-label={`Elimina definitivamente ${v.nome}`}
                               onClick={() => { setErrore(null); setMessaggio(null); setDaEliminare(v) }}>
                        Elimina definitivamente
                      </Bottone>
                    </div>
                  )}
                </td>
              </tr>
            )
          })}
        </Tabella>
      )}
      {daRecuperare && q && (
        <ChiediRecupero voce={daRecuperare} tempi={q.tempi_glacier} ore={q.ore_zip} chiudi={() => setDaRecuperare(null)}
          fatto={(testo) => { setDaRecuperare(null); setMessaggio(testo); carica() }} />
      )}
      {daEliminare && (
        <EliminaDefinitivamente voce={daEliminare} chiudi={() => setDaEliminare(null)}
          fatto={(testo) => { setDaEliminare(null); setMessaggio(testo); carica() }} />
      )}
      {daScaricare && (
        <ScaricaZip voce={daScaricare.voce} recupero={daScaricare.recupero}
          chiudi={() => { setDaScaricare(null); carica() }} />
      )}
    </div>
  )
}

function ChiediRecupero({ voce, tempi, ore, chiudi, fatto }: {
  voce: posta.VoceQuarantena; tempi: posta.Quarantena['tempi_glacier']; ore: number
  chiudi: () => void; fatto: (testo: string) => void
}) {
  const [motivo, setMotivo] = useState('')
  const [consapevole, setConsapevole] = useState(false)
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  async function chiedi() {
    setLavoro(true); setErrore(null)
    try {
      await posta.chiediRecuperoSicuro(voce.id, motivo.trim())
      fatto(`Recupero chiesto per «${voce.nome}». Quando lo zip è pronto ricevi un avviso: di solito entro `
        + `${tempi.tipicoOre} ore, al più entro ${tempi.massimoOre}. La Referente privacy è stata avvisata.`)
    } catch (e) { setErrore(testoErrore(e)) } finally { setLavoro(false) }
  }

  return (
    <Modale titolo="Scarica in formato sicuro" chiudi={chiudi}
      azioni={
        <>
          <Bottone variante="primario" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante="secondario" inCorso={lavoro} disabled={!consapevole || motivo.trim().length < 10}
                   onClick={() => void chiedi()}>
            Chiedi il recupero
          </Bottone>
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <Avviso tipo="errore">
        <strong>{voce.nome}</strong>: {PERICOLO}
      </Avviso>
      <ul className="piccolo">
        <li>il file torna da Glacier: di solito entro {tempi.tipicoOre} ore, al più entro {tempi.massimoOre}; ricevi un avviso quando è pronto;</li>
        <li>arriva come zip cifrato con password (AES-256), con dentro il file, rinominato con «.infetto» in coda, e
          un LEGGIMI-PERICOLO.txt; Esplora file di Windows e Archivio Utility del Mac non lo aprono, serve un
          programma come 7-Zip;</li>
        <li>lo zip si scarica solo da te, per {ore} ore; la password si mostra una volta, al primo scaricamento;</li>
        <li>non scaricarlo se non in questo formato, non aprirlo né estrarlo sul computer di lavoro, non inoltrarlo
          per email: si consegna lo zip a chi lo analizza o all’autorità, con la password comunicata a parte;</li>
        <li>la richiesta e ogni scaricamento restano nel registro eventi, e la Referente privacy riceve un avviso.</li>
      </ul>
      <AreaTesto etichetta="Motivo" valore={motivo} cambia={setMotivo} righe={3} obbligatorio maxLength={1000}
                 aiuto="Per chi è e perché: resta nel registro e lo legge la Referente privacy. Almeno 10 caratteri." />
      <Spunta etichetta="Ho letto gli avvisi: contiene un programma dannoso e non lo apro sul computer di lavoro."
              valore={consapevole} cambia={setConsapevole} />
    </Modale>
  )
}

function ScaricaZip({ voce, recupero, chiudi }: {
  voce: posta.VoceQuarantena; recupero: posta.RecuperoSicuro; chiudi: () => void
}) {
  const [consapevole, setConsapevole] = useState(false)
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<{ password: string | null; nome_zip: string } | null>(null)
  const [copiata, setCopiata] = useState(false)

  async function scarica() {
    setLavoro(true); setErrore(null)
    try {
      const r = await posta.scaricaRecuperoSicuro(recupero.id)
      setEsito({ password: r.password, nome_zip: r.nome_zip })
      // Lo zip ha Content-Disposition attachment: la pagina resta, con la password in vista.
      window.location.assign(r.url)
    } catch (e) { setErrore(testoErrore(e)) } finally { setLavoro(false) }
  }

  async function copia(password: string) {
    try { await navigator.clipboard.writeText(password); setCopiata(true) } catch {
      setErrore('Il browser non ha permesso di copiare: scrivila a mano.')
    }
  }

  return (
    <Modale titolo="Scarica lo zip cifrato" chiudi={chiudi} fuocoIniziale="azioni"
      azioni={esito
        ? <Bottone variante="primario" onClick={chiudi}>Ho annotato la password, chiudi</Bottone>
        : (
          <>
            <Bottone variante="primario" onClick={chiudi}>Annulla</Bottone>
            <Bottone variante="secondario" inCorso={lavoro} disabled={!consapevole} onClick={() => void scarica()}>
              Scarica lo zip
            </Bottone>
          </>
        )}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <Avviso tipo="errore"><strong>{voce.nome}</strong>: {PERICOLO}</Avviso>
      {!esito && (
        <>
          <p className="piccolo">
            Lo zip è cifrato con password (AES-256) e contiene il file, rinominato con «.infetto» in coda, e un
            LEGGIMI-PERICOLO.txt. Salvalo dove lo prenderà chi lo analizza; non estrarlo su questo computer.
            {recupero.password_da_mostrare
              ? ' La password compare qui, una volta sola: annotala prima di chiudere.'
              : ' La password è già stata mostrata al primo scaricamento: se l’hai persa, chiudi e chiedi un recupero nuovo quando questo scade.'}
          </p>
          {recupero.impronta_file && (
            <p className="piccolo tenue">SHA-256 del file: <span className="mono">{recupero.impronta_file}</span></p>
          )}
          <Spunta etichetta="Ho capito: lo scarico solo in questo formato e non lo apro sul computer di lavoro."
                  valore={consapevole} cambia={setConsapevole} />
        </>
      )}
      {esito && (
        <>
          <p>Lo scaricamento di <span className="mono">{esito.nome_zip}</span> è partito.</p>
          {esito.password ? (
            <>
              <p>Password dello zip, mostrata solo ora:</p>
              <p className="mono quarantena-password">{esito.password}</p>
              <Bottone dimensione="piccolo" variante="discreto" onClick={() => void copia(esito.password!)}>
                {copiata ? 'Copiata' : 'Copia la password'}
              </Bottone>
              <p className="piccolo">
                Comunicala a chi riceve lo zip per un’altra strada (a voce, al telefono), non nella stessa email.
              </p>
            </>
          ) : (
            <p className="piccolo">La password è già stata mostrata al primo scaricamento.</p>
          )}
          <p className="piccolo testo-critico">
            Non aprire né estrarre lo zip sul computer di lavoro. Lo scaricamento resta nel registro eventi e la
            Referente privacy ha ricevuto un avviso.
          </p>
        </>
      )}
    </Modale>
  )
}

function EliminaDefinitivamente({ voce, chiudi, fatto }: {
  voce: posta.VoceQuarantena; chiudi: () => void; fatto: (testo: string) => void
}) {
  const [nome, setNome] = useState('')
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const leggibile = voce.nome !== '(nome non leggibile)'
  const atteso = leggibile ? voce.nome : 'ELIMINA'
  const uguale = nome.normalize('NFC').trim().toLowerCase() === atteso.normalize('NFC').trim().toLowerCase()

  async function elimina() {
    setLavoro(true); setErrore(null)
    try {
      await posta.eliminaDallaQuarantena(voce.id, nome)
      fatto(`«${voce.nome}» eliminato definitivamente dalla quarantena. Nei messaggi resta il nome, con chi l’ha eliminato e quando.`)
    } catch (e) { setErrore(testoErrore(e)) } finally { setLavoro(false) }
  }

  return (
    <Modale titolo="Elimina definitivamente" chiudi={chiudi}
      azioni={
        <>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante="critico" inCorso={lavoro} disabled={!uguale} onClick={() => void elimina()}>
            Elimina definitivamente
          </Bottone>
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p>
        Il file <strong>{voce.nome}</strong> si cancella dalla quarantena e non si recupera più, nemmeno in formato
        sicuro. I recuperi in corso si annullano. Nei messaggi resta il nome dell’allegato, con «eliminato dalla
        quarantena» e chi l’ha fatto; l’eliminazione resta nel registro eventi.
      </p>
      <Campo etichetta={leggibile ? 'Per confermare, scrivi il nome del file' : 'Il nome non si legge più: scrivi ELIMINA'}
             valore={nome} cambia={setNome} obbligatorio autoComplete="off" segnaposto={atteso} />
    </Modale>
  )
}
